import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Trash2, 
  Play, 
  Pause, 
  Users,
  Activity,
  ExternalLink,
  Search,
  FolderKanban,
  Radio,
  Eye,
  RefreshCw,
  Tags
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { safeLocalStorage } from "@/utils/safeStorage";
import { TELEGRAM_CATEGORIES } from "@/constants/telegramCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = safeLocalStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to handle API errors
const handleApiError = (error, navigate, t) => {
  if (error.response?.status === 401) {
    toast.error(t('errors.sessionExpired') || 'انتهت الجلسة - يرجى تسجيل الدخول');
    safeLocalStorage.removeItem('auth_token');
    safeLocalStorage.removeItem('user');
    navigate('/login');
  } else {
    toast.error(t('errors.serverError'));
  }
};

const GroupCard = ({ group, onDelete, onStartMonitoring, onStopMonitoring, onExtractMembers, extracting, monitoring, monitorStatus, t, isArabic }) => {
  const isMonitoring = group.status === 'monitoring' || monitoring === group.id;
  const isExtracting = group.status === 'extracting' || extracting === group.id;
  const statusData = monitorStatus[group.id];
  
  // Get category info
  const category = TELEGRAM_CATEGORIES.find(c => c.id === group.category);
  
  return (
    <div 
      data-testid={`group-card-${group.id}`}
      className={`glass rounded-xl p-6 card-hover ${isMonitoring ? 'tracing-beam' : ''}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">{group.group_name}</h3>
          <a 
            href={group.group_link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1"
          >
            <ExternalLink size={14} />
            {group.group_link}
          </a>
          {/* Category Badge */}
          {category && (
            <div 
              className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-full text-xs text-white"
              style={{ backgroundColor: category.color }}
            >
              <Tags size={12} />
              {isArabic ? category.name_ar : category.name_en}
            </div>
          )}
        </div>
        <div className={`badge ${isExtracting ? 'badge-info' : isMonitoring ? 'badge-success' : 'badge-warning'}`}>
          {isExtracting ? t('groups.extracting') : isMonitoring ? (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              {t('groups.activeMonitoring') || 'مراقبة نشطة'}
            </span>
          ) : t('groups.stopped')}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-black/30 rounded-lg p-3">
          <div className="flex items-center gap-2 text-neutral-500 text-sm mb-1">
            <Users size={14} />
            {t('members.title')}
          </div>
          <p className="font-mono text-xl">{group.members_count || 0}</p>
        </div>
        <div className="bg-black/30 rounded-lg p-3">
          <div className="flex items-center gap-2 text-neutral-500 text-sm mb-1">
            <Activity size={14} />
            {t('groups.activeMembers')}
          </div>
          <p className="font-mono text-xl text-green-400">{group.active_members || 0}</p>
        </div>
      </div>

      {/* Show monitoring stats when active */}
      {isMonitoring && statusData?.is_monitoring && (
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg p-3 mb-4 border border-green-500/20">
          <div className="flex items-center justify-between text-sm">
            <span className="text-green-400 flex items-center gap-2">
              <Radio size={14} className="animate-pulse" />
              {t('groups.monitoringActive') || 'جاري جمع الأعضاء النشطين...'}
            </span>
            <span className="text-neutral-400">
              {statusData.messages_processed || 0} {t('groups.messagesProcessed') || 'رسالة'}
            </span>
          </div>
          {statusData.members_found > 0 && (
            <div className="mt-2 text-xs text-emerald-400">
              +{statusData.members_found} {t('groups.newMembersFound') || 'عضو جديد'}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {/* Monitor Button - Primary action */}
        <Button
          data-testid={`monitor-${group.id}`}
          variant={isMonitoring ? "secondary" : "default"}
          className={`flex-1 ${isMonitoring ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600'}`}
          onClick={() => isMonitoring ? onStopMonitoring(group.id) : onStartMonitoring(group.id)}
          disabled={isExtracting}
        >
          {isMonitoring ? (
            <>
              <Radio size={16} className="ml-2 animate-pulse" />
              {t('groups.stopMonitoring') || 'إيقاف المراقبة'}
            </>
          ) : (
            <>
              <Eye size={16} className="ml-2" />
              {t('groups.startMonitoring') || 'بدء المراقبة'}
            </>
          )}
        </Button>
        
        {/* Extract Button */}
        <Button
          data-testid={`extract-members-${group.id}`}
          variant="outline"
          className="bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20"
          onClick={() => onExtractMembers(group.id)}
          disabled={isExtracting || isMonitoring}
          title={isMonitoring ? (t('groups.stopMonitoringFirst') || 'أوقف المراقبة أولاً') : ''}
        >
          {isExtracting ? (
            <>
              <div className="spinner-small ml-2" />
              {t('groups.extracting')}
            </>
          ) : (
            <>
              <Users size={16} className="ml-2" />
              {t('groups.extractMembers')}
            </>
          )}
        </Button>
        
        {/* Delete Button */}
        <Button
          data-testid={`delete-group-${group.id}`}
          variant="destructive"
          size="icon"
          onClick={() => onDelete(group.id)}
          disabled={isExtracting || isMonitoring}
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
};

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ group_link: '', group_name: '', category: '' });
  const [extractingGroup, setExtractingGroup] = useState(null);
  const [monitoringGroup, setMonitoringGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [monitorStatus, setMonitorStatus] = useState({});
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n?.language === 'ar';

  const fetchSubscriptionUsage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/usage`, { headers: getAuthHeaders() });
      setSubscriptionUsage(response.data);
    } catch (error) {
      // Ignore errors
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`, { headers: getAuthHeaders() });
      setGroups(response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, navigate, t);
      return [];
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  // Fetch monitor status for groups that are monitoring
  const fetchMonitorStatuses = useCallback(async (groupsList) => {
    const monitoringGroups = groupsList.filter(g => g.status === 'monitoring');
    if (monitoringGroups.length === 0) return;

    const statuses = {};
    for (const group of monitoringGroups) {
      try {
        const response = await axios.get(
          `${API}/telegram/monitor-status/${group.id}`,
          { headers: getAuthHeaders() }
        );
        statuses[group.id] = response.data;
      } catch (e) {
        // Ignore errors for individual status checks
      }
    }
    setMonitorStatus(statuses);
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchSubscriptionUsage();
  }, [fetchGroups, fetchSubscriptionUsage]);

  // Poll for updates when there are monitoring groups
  useEffect(() => {
    const hasMonitoringGroups = groups.some(g => g.status === 'monitoring');
    if (!hasMonitoringGroups) return;

    // Initial fetch
    fetchMonitorStatuses(groups);

    // Poll every 5 seconds
    const interval = setInterval(async () => {
      const updatedGroups = await fetchGroups();
      await fetchMonitorStatuses(updatedGroups);
    }, 5000);

    return () => clearInterval(interval);
  }, [groups.length, fetchGroups, fetchMonitorStatuses]);

  const handleAddGroup = async () => {
    if (!newGroup.group_link) {
      toast.error(t('errors.required'));
      return;
    }
    
    if (!newGroup.category) {
      toast.error(isArabic ? 'يرجى اختيار فئة المجموعة' : 'Please select a group category');
      return;
    }

    try {
      await axios.post(`${API}/groups`, newGroup, { headers: getAuthHeaders() });
      toast.success(t('groups.groupAdded'));
      setShowAddModal(false);
      setNewGroup({ group_link: '', group_name: '', category: '' });
      fetchGroups();
    } catch (error) {
      handleApiError(error, navigate, t);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm(t('groups.confirmDeleteGroup'))) return;

    try {
      await axios.delete(`${API}/groups/${groupId}`, { headers: getAuthHeaders() });
      toast.success(t('groups.groupDeleted'));
      fetchGroups();
    } catch (error) {
      handleApiError(error, navigate, t);
    }
  };

  const handleStartMonitoring = async (groupId) => {
    // Check if user has reached their limit first
    if (subscriptionUsage && !subscriptionUsage.can_extract) {
      toast.error(subscriptionUsage.message || (isArabic 
        ? 'لقد وصلت للحد الأقصى. يرجى ترقية خطتك.'
        : 'You have reached your limit. Please upgrade your plan.'));
      return;
    }
    
    try {
      setMonitoringGroup(groupId);
      const response = await axios.post(
        `${API}/telegram/start-monitoring`, 
        { group_id: groupId },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(t('groups.monitoringStarted'));
        fetchGroups();
      } else if (response.data.limit_reached) {
        toast.error(response.data.message || t('subscription.limitReached'));
        fetchSubscriptionUsage();
      } else if (response.data.needs_auth) {
        toast.error(t('groups.needsAuth'));
        navigate('/settings');
      } else {
        toast.error(response.data.message || t('errors.serverError'));
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        handleApiError(error, navigate, t);
      }
    } finally {
      setMonitoringGroup(null);
    }
  };

  const handleStopMonitoring = async (groupId) => {
    try {
      setMonitoringGroup(groupId);
      const response = await axios.post(
        `${API}/telegram/stop-monitoring`, 
        { group_id: groupId },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(t('groups.monitoringStopped'));
        fetchGroups();
      } else {
        toast.error(t('errors.serverError'));
      }
    } catch (error) {
      handleApiError(error, navigate, t);
    } finally {
      setMonitoringGroup(null);
    }
  };

  const handleToggleMonitoring = async (groupId, start) => {
    try {
      const endpoint = start ? 'start-monitoring' : 'stop-monitoring';
      await axios.post(`${API}/groups/${groupId}/${endpoint}`, {}, { headers: getAuthHeaders() });
      toast.success(start ? t('groups.monitoringStarted') : t('groups.monitoringStopped'));
      fetchGroups();
    } catch (error) {
      handleApiError(error, navigate, t);
    }
  };

  const handleExtractMembers = async (groupId) => {
    try {
      setExtractingGroup(groupId);
      
      const response = await axios.post(
        `${API}/telegram/extract-members`, 
        { group_id: groupId, limit: 1000 },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || t('groups.extractSuccess'));
        fetchGroups();
        fetchSubscriptionUsage(); // Refresh usage after extraction
      } else if (response.data.limit_reached) {
        // User has reached their subscription limit
        toast.error(response.data.message || t('subscription.limitReached'));
        fetchSubscriptionUsage();
      } else if (response.data.needs_auth) {
        toast.error(t('groups.needsAuth'));
        navigate('/settings');
      } else {
        toast.error(response.data.message || t('errors.serverError'));
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        handleApiError(error, navigate, t);
      }
    } finally {
      setExtractingGroup(null);
    }
  };

  const filteredGroups = groups.filter(g => {
    // If no search term, show all groups
    if (!searchTerm.trim()) return true;
    
    const term = searchTerm.toLowerCase().trim();
    const groupName = (g.group_name || '').toLowerCase();
    const groupLink = (g.group_link || '').toLowerCase();
    const category = (g.category || '').toLowerCase();
    
    return groupName.includes(term) || groupLink.includes(term) || category.includes(term);
  });

  // Check if user has reached limit
  const hasReachedLimit = subscriptionUsage && !subscriptionUsage.can_extract;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="groups-page" className="space-y-6 animate-fade-in">
      {/* Subscription Limit Warning */}
      {hasReachedLimit && (
        <div 
          data-testid="subscription-limit-warning"
          className="p-4 bg-red-500/20 border-2 border-red-500 rounded-xl"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-500/30 flex items-center justify-center">
                <Users size={24} className="text-red-400" />
              </div>
              <div>
                <p className="text-red-400 font-bold text-lg">
                  ⚠️ {isArabic ? 'تم الوصول للحد الأقصى!' : 'Limit Reached!'}
                </p>
                <p className="text-red-300 text-sm">
                  {isArabic 
                    ? (subscriptionUsage?.message_ar || `لقد استخرجت ${subscriptionUsage?.extracted_count?.toLocaleString()} من ${subscriptionUsage?.limit?.toLocaleString()} عضو`)
                    : (subscriptionUsage?.message_en || `You've extracted ${subscriptionUsage?.extracted_count?.toLocaleString()} of ${subscriptionUsage?.limit?.toLocaleString()} members`)
                  }
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/subscription')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white font-bold px-6"
            >
              🚀 {isArabic ? 'ترقية الخطة الآن' : 'Upgrade Now'}
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide">{t('groups.title')}</h1>
          <p className="text-neutral-500 mt-1">{t('groups.monitoredGroups')}</p>
        </div>
        <Button 
          data-testid="add-group-btn"
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus size={18} className="ml-2" />
          {t('groups.addGroup')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
        <Input
          data-testid="search-groups"
          placeholder={t('groups.searchGroups')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pr-10"
        />
      </div>

      {/* Groups Grid */}
      {filteredGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              onDelete={handleDeleteGroup}
              onStartMonitoring={handleStartMonitoring}
              onStopMonitoring={handleStopMonitoring}
              onExtractMembers={handleExtractMembers}
              extracting={extractingGroup}
              monitoring={monitoringGroup}
              monitorStatus={monitorStatus}
              t={t}
              isArabic={isArabic}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state glass rounded-xl">
          <FolderKanban className="empty-state-icon mx-auto text-neutral-600" />
          <h3 className="text-lg font-semibold text-neutral-400">{t('groups.noGroups')}</h3>
          <p className="text-neutral-500 mt-2">{t('groups.addGroupToStart')}</p>
        </div>
      )}

      {/* Add Group Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent 
          className="glass"
          style={{
            border: '2px solid #FFFFFF',
            borderRadius: '16px',
            boxShadow: '0 0 20px rgba(255, 255, 255, 0.3)'
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('groups.addNewGroup')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-neutral-400 block mb-2">{t('groups.groupLinkRequired')}</label>
              <Input
                data-testid="group-link-input"
                placeholder="https://t.me/group_name"
                value={newGroup.group_link}
                onChange={(e) => setNewGroup({ ...newGroup, group_link: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm text-neutral-400 block mb-2">{t('groups.groupNameOptional')}</label>
              <Input
                data-testid="group-name-input"
                placeholder={t('groups.displayName')}
                value={newGroup.group_name}
                onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
              />
            </div>
            {/* Category Selection */}
            <div>
              <label className="text-sm font-bold text-white block mb-2">
                <Tags size={14} className="inline ml-1" />
                {isArabic ? 'فئة المجموعة *' : 'Group Category *'}
              </label>
              <Select 
                value={newGroup.category} 
                onValueChange={(val) => setNewGroup({ ...newGroup, category: val })}
              >
                <SelectTrigger 
                  data-testid="group-category-select"
                  className="w-full bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium [&>span]:text-white [&>span]:opacity-100 [&>svg]:text-white [&>svg]:opacity-100"
                >
                  <SelectValue 
                    placeholder={isArabic ? 'اختر فئة المجموعة...' : 'Select group category...'} 
                    className="text-white"
                  />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {TELEGRAM_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        ></div>
                        <span>{isArabic ? cat.name_ar : cat.name_en}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                {isArabic 
                  ? 'اختيار الفئة يساعد في تصنيف الأعضاء المستخرجين تلقائياً'
                  : 'Selecting a category helps auto-classify extracted members'
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              data-testid="confirm-add-group" 
              onClick={handleAddGroup} 
              className="btn-primary"
              disabled={!newGroup.category}
            >
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
